import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { panSuppressesClick } from "../combatPan";
import type { View } from "./types";

export type StagePan = {
  /** Set on a genuine pan-drag's release so `onStageClick` swallows the trailing click. */
  suppressClickRef: RefObject<boolean>;
  /** A pan-drag in flight on ANY button — drives the `grabbing` cursor for the whole gesture. */
  panning: boolean;
  /** Is a pan currently open? (`panRef` is non-null between `beginPan` and `onPanEnd`.) */
  panRef: RefObject<{ button: number; startClientX: number; startClientY: number; startViewX: number; startViewY: number; moved: boolean } | null>;
  beginPan: (e: MouseEvent) => void;
  onPanEnd: () => void;
};

/**
 * Manual pan-drag — ONE mechanism behind every way in (middle-always, right-always,
 * left-when-`canPanLeft`); which button opens it is decided by `combatPan`'s `startsPan`, and
 * nothing here is button-specific except the trailing-click swallow. Record the press point +
 * the view at press time, then a WINDOW-level mousemove/mouseup pair (removed on release) mirrors
 * the delta into `view` and streams the live cursor. `panRef.moved` past a tiny screen-px
 * threshold marks the gesture a real pan (not a stationary press) — `onStageClick` swallows the
 * trailing click when `panSuppressesClick` says one is coming, so a left pan-drag never also
 * fires a click (and a middle/right drag, which raises `auxclick` and no `click` at all, never
 * arms a swallow that would then eat the NEXT left click).
 */
export function useStagePan({
  view,
  setView,
  cancelPing,
  streamCursor,
  screenPoint,
  worldPoint,
}: {
  view: View;
  setView: (next: View | ((v: View) => View)) => void;
  cancelPing: () => void;
  streamCursor: (world: { x: number; y: number } | null) => void;
  screenPoint: (e: MouseEvent | WheelEvent) => { x: number; y: number } | null;
  worldPoint: (screen: { x: number; y: number } | null) => { x: number; y: number } | null;
}): StagePan {
  const panRef = useRef<{ button: number; startClientX: number; startClientY: number; startViewX: number; startViewY: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  // Drives the `grabbing` cursor for the whole drag. `.combat-stage-pan:active` already covers the
  // left-button hand, but `:active` never fires for a middle/right press — so those two need real
  // state to show the same cursor the space-held pan does.
  const [panning, setPanning] = useState(false);
  const onPanMove = useCallback(
    (e: MouseEvent) => {
      const p = panRef.current;
      if (!p) return;
      const dx = e.clientX - p.startClientX;
      const dy = e.clientY - p.startClientY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) p.moved = true;
      setView((v) => ({ ...v, x: p.startViewX + dx, y: p.startViewY + dy }));
      cancelPing();
      const world = worldPoint(screenPoint(e));
      if (world) streamCursor(world);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the dep list is carried over verbatim:
    // `setView`/`screenPoint`/`worldPoint` are deliberately absent so this callback's identity stays
    // stable across a viewport resize mid-drag (see `setView`'s doc in CombatStage).
    [cancelPing, streamCursor],
  );
  const onPanEnd = useCallback(() => {
    window.removeEventListener("mousemove", onPanMove);
    window.removeEventListener("mouseup", onPanEnd);
    const p = panRef.current;
    if (p && panSuppressesClick(p.button, p.moved)) suppressClickRef.current = true;
    panRef.current = null;
    setPanning(false);
  }, [onPanMove]);
  const beginPan = useCallback(
    (e: MouseEvent) => {
      panRef.current = { button: e.button, startClientX: e.clientX, startClientY: e.clientY, startViewX: view.x, startViewY: view.y, moved: false };
      setPanning(true);
      window.addEventListener("mousemove", onPanMove);
      window.addEventListener("mouseup", onPanEnd);
    },
    [view.x, view.y, onPanMove, onPanEnd],
  );
  // A pan drag in flight when the surface unmounts mid-gesture would otherwise leak window listeners.
  useEffect(() => () => onPanEnd(), [onPanEnd]);

  return { suppressClickRef, panning, panRef, beginPan, onPanEnd };
}
